import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  IconButton,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  ContentCopy as DuplicateIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '90vw',
    maxHeight: '90vh',
  },
}));

const DuplicateGroup = styled(Box)(({ theme }) => ({
  border: '1px solid #333',
  borderRadius: '8px',
  marginBottom: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
}));

const QuestionPreview = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid #444',
  borderRadius: '6px',
  padding: '12px',
  marginBottom: '8px',
}));

// Utility function to calculate text similarity
const calculateSimilarity = (text1, text2) => {
  const normalize = (str) => str.toLowerCase().replace(/[^\w\s]/g, '').trim();
  const normalized1 = normalize(text1);
  const normalized2 = normalize(text2);
  
  if (normalized1 === normalized2) return 1.0;
  
  // Simple word-based similarity
  const words1 = normalized1.split(/\s+/);
  const words2 = normalized2.split(/\s+/);
  
  const allWords = new Set([...words1, ...words2]);
  const intersection = words1.filter(word => words2.includes(word));
  
  return intersection.length / allWords.size;
};

// Function to detect duplicates
const detectDuplicates = (questions, threshold = 0.8) => {
  const duplicateGroups = [];
  const processed = new Set();
  
  questions.forEach((question, index) => {
    if (processed.has(index)) return;
    
    const duplicates = [{ question, index }];
    
    for (let i = index + 1; i < questions.length; i++) {
      if (processed.has(i)) continue;
      
      const similarity = calculateSimilarity(
        question.questionText,
        questions[i].questionText
      );
      
      if (similarity >= threshold) {
        duplicates.push({ question: questions[i], index: i });
        processed.add(i);
      }
    }
    
    if (duplicates.length > 1) {
      duplicateGroups.push(duplicates);
      duplicates.forEach(({ index }) => processed.add(index));
    }
  });
  
  return duplicateGroups;
};

const DuplicateManagement = ({ 
  open, 
  onClose, 
  questions, 
  onRemoveDuplicates,
  similarityThreshold = 0.8 
}) => {
  const [selectedForRemoval, setSelectedForRemoval] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);

  // Detect duplicate groups
  const duplicateGroups = useMemo(() => {
    return detectDuplicates(questions, similarityThreshold);
  }, [questions, similarityThreshold]);

  // Calculate statistics
  const totalDuplicates = duplicateGroups.reduce((sum, group) => sum + group.length - 1, 0);
  const totalQuestions = duplicateGroups.reduce((sum, group) => sum + group.length, 0);

  // Handle individual question selection
  const toggleQuestionSelection = (questionIndex) => {
    const newSelected = new Set(selectedForRemoval);
    if (newSelected.has(questionIndex)) {
      newSelected.delete(questionIndex);
    } else {
      newSelected.add(questionIndex);
    }
    setSelectedForRemoval(newSelected);
  };

  // Handle group selection (keep first, remove others)
  const selectGroupDuplicates = (group, keepFirst = true) => {
    const newSelected = new Set(selectedForRemoval);
    group.forEach(({ index }, i) => {
      if (keepFirst && i === 0) {
        newSelected.delete(index); // Keep the first one
      } else if (!keepFirst || i > 0) {
        newSelected.add(index); // Remove duplicates
      }
    });
    setSelectedForRemoval(newSelected);
  };

  // Handle select all duplicates
  const handleSelectAllDuplicates = () => {
    if (selectAllMode) {
      setSelectedForRemoval(new Set());
    } else {
      const allDuplicateIndices = new Set();
      duplicateGroups.forEach(group => {
        // Keep first, remove others
        group.slice(1).forEach(({ index }) => {
          allDuplicateIndices.add(index);
        });
      });
      setSelectedForRemoval(allDuplicateIndices);
    }
    setSelectAllMode(!selectAllMode);
  };

  // Handle removal
  const handleRemoveDuplicates = () => {
    const indicesToRemove = Array.from(selectedForRemoval).sort((a, b) => b - a);
    onRemoveDuplicates(indicesToRemove);
    onClose();
  };

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setSelectedForRemoval(new Set());
      setSelectAllMode(false);
    }
  }, [open]);

  if (duplicateGroups.length === 0) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DuplicateIcon sx={{ color: '#4CAF50' }} />
            <Typography variant="h6">Duplicate Detection</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="success" sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
            No duplicate questions found! All questions appear to be unique.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #333' }}>
          <Button onClick={onClose} sx={{ color: '#4CAF50' }}>
            Close
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        color: '#fff', 
        borderBottom: '1px solid #333',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon sx={{ color: '#ff9800' }} />
          <Typography variant="h6">Duplicate Questions Found</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#ccc' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Summary */}
        <Alert severity="warning" sx={{ mb: 3, backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Found {duplicateGroups.length} duplicate groups affecting {totalQuestions} questions
          </Typography>
          <Typography variant="body2">
            You can remove {totalDuplicates} duplicate questions to keep only unique ones.
          </Typography>
        </Alert>

        {/* Bulk Actions */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllMode}
                onChange={handleSelectAllDuplicates}
                sx={{ color: '#4CAF50' }}
              />
            }
            label={`Select all duplicates (keep first occurrence of each)`}
            sx={{ color: '#fff', mb: 1 }}
          />
          <Typography variant="body2" sx={{ color: '#ccc', ml: 4 }}>
            This will select {totalDuplicates} questions for removal, keeping the first occurrence of each duplicate group.
          </Typography>
        </Box>

        {/* Duplicate Groups */}
        <Box>
          {duplicateGroups.map((group, groupIndex) => (
            <DuplicateGroup key={groupIndex}>
              <Accordion 
                sx={{ 
                  backgroundColor: 'transparent',
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: '#4CAF50' }} />}
                  sx={{ borderBottom: '1px solid #333' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                    <Typography variant="subtitle1" sx={{ color: '#fff' }}>
                      Duplicate Group {groupIndex + 1} ({group.length} questions)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectGroupDuplicates(group, true);
                        }}
                        sx={{ color: '#4CAF50' }}
                      >
                        Keep First
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectGroupDuplicates(group, false);
                        }}
                        sx={{ color: '#f44336' }}
                      >
                        Remove All
                      </Button>
                    </Box>
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <List>
                    {group.map(({ question, index }, itemIndex) => (
                      <ListItem key={index} sx={{ py: 1 }}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedForRemoval.has(index)}
                                  onChange={() => toggleQuestionSelection(index)}
                                  sx={{ color: '#f44336' }}
                                />
                              }
                              label={`Remove this question`}
                              sx={{ color: '#fff' }}
                            />
                            {itemIndex === 0 && (
                              <Chip 
                                label="Original" 
                                size="small" 
                                sx={{ ml: 2, backgroundColor: '#4CAF50', color: '#fff' }}
                              />
                            )}
                          </Box>
                          
                          <QuestionPreview>
                            <Typography variant="body2" sx={{ color: '#fff', mb: 1 }}>
                              <strong>Row {question.rowNumber}:</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#ccc' }}>
                              {question.questionText.length > 150 
                                ? `${question.questionText.substring(0, 150)}...`
                                : question.questionText
                              }
                            </Typography>
                          </QuestionPreview>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </DuplicateGroup>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #333' }}>
        <Typography variant="body2" sx={{ color: '#ccc', mr: 'auto' }}>
          {selectedForRemoval.size} questions selected for removal
        </Typography>
        <Button onClick={onClose} sx={{ color: '#ccc' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleRemoveDuplicates}
          disabled={selectedForRemoval.size === 0}
          sx={{ 
            backgroundColor: '#f44336',
            color: '#fff',
            '&:hover': { backgroundColor: '#d32f2f' },
            '&:disabled': { backgroundColor: '#333', color: '#666' }
          }}
        >
          Remove Selected ({selectedForRemoval.size})
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default DuplicateManagement;
